import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DevisStatut, IDevisRequest, IDevisResponse } from 'src/app/shared/models/devis';
import { DevisService } from '../devis.service';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { UserProfile } from 'src/app/shared/models/user-profile';

@Component({
  selector: 'app-devis-dialog',
  templateUrl: './devis-dialog.component.html',
  styleUrls: ['./devis-dialog.component.less']
})
export class DevisDialogComponent implements OnInit {

  devisForm: FormGroup;
  readonly DevisStatut = DevisStatut;
  initialValue: any;

  constructor(
    private fb: FormBuilder,
    private devisService: DevisService,
    public authenticationService: AuthenticationService,
    private snackbarService: SnackbarService,
    public dialogRef: MatDialogRef<DevisDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IDevisResponse
  ) {
    this.devisForm = this.fb.group({
      devisMajore:          [data.devisMajore ?? false],
      devisDateDemarrageGo: [data.devisDateDemarrageGo ? new Date(data.devisDateDemarrageGo) : null],
      devisDuree:           [data.devisDuree ?? null],
      devisProvisionFrais:  [data.devisProvisionFrais ?? null],
      devisPrixUoPropose:   [data.devisPrixUoPropose ?? (data.devisMajore ? data.febPrixUoGrilleMajore : data.febPrixUoGrille) ?? null],
      devisCommentaire:     [data.devisCommentaire ?? null],
      devisNumeroContrat:   [data.devisNumeroContrat ?? null],
      devisNomContrat:      [data.devisNomContrat ?? null],
      devisBeneficiaire:    [{ value: data.devisBeneficiaire ?? null, disabled: true }],
      devisDateEntretien:   [
        data.dateEntretien ? new Date(data.dateEntretien) : null,
        Validators.required
      ]
    });
  }

  ngOnInit(): void {

    if (!this.canEdit()) {
      this.devisForm.disable();
    }

    this.devisForm.get('devisMajore')!.valueChanges.subscribe((majore: boolean) => {
      const prix = majore ? this.data.febPrixUoGrilleMajore : this.data.febPrixUoGrille;
      const prixArrondi = prix != null ? Math.round(prix * 100) / 100 : null;
      this.devisForm.get('devisPrixUoPropose')!
        .setValue(prixArrondi, { emitEvent: false });
    });

    this.devisForm.get('devisPrixUoPropose')!.valueChanges.subscribe((val: any) => {
      if (val != null && !isNaN(val)) {
        const arrondi = Math.round(+val * 100) / 100;
        if (+val !== arrondi) {
          this.devisForm.get('devisPrixUoPropose')!
            .setValue(arrondi, { emitEvent: false });
        }
      }
    });

    Promise.all([
      this.devisService.getNumContrat().toPromise(),
      this.devisService.getNomContrat().toPromise()
    ]).then(([numContrat, nomContrat]) => {

      this.devisForm.get('devisNumeroContrat')?.setValue(numContrat, { emitEvent: false });
      this.devisForm.get('devisNumeroContrat')?.disable();

      this.devisForm.get('devisNomContrat')?.setValue(nomContrat, { emitEvent: false });
      this.devisForm.get('devisNomContrat')?.disable();

      this.initialValue = structuredClone(this.devisForm.getRawValue());

      this.devisForm.markAsPristine();
    });
  }

  hasChanges(): boolean {
    if (!this.initialValue) return false;

    const current = this.devisForm.getRawValue();

    return Object.keys(current).some(key =>
      current[key] !== this.initialValue[key]
    );
  }

  isCdcOrAdmin(): boolean {
    const profile = this.authenticationService.getUser()?.profile;
    return this.authenticationService.isUserAdmin()
      || profile === UserProfile.MANAGER_CDS
      || profile === UserProfile.CP_CDS;
  }

  canEdit(): boolean {
    if (this.authenticationService.isUserAdmin()) return true;
    return this.isCdcOrAdmin() && this.data.devisStatut === DevisStatut.CREE;
  }

  canSendToSncf(): boolean {
    return this.isCdcOrAdmin() && this.data.devisStatut === DevisStatut.CREE;
  }

  private toLocalDateStr(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private dateToStr(val: any): string | null {
    if (!val) return null;
    if (val instanceof Date) return this.toLocalDateStr(val);
    return val;
  }

  onSave(): void {
    this.devisForm.markAllAsTouched();
    if (this.devisForm.invalid) return;

    const raw = this.devisForm.getRawValue();

    const request: IDevisRequest = {
      devisMajore:          raw.devisMajore ?? null,
      devisDateDemarrageGo: this.dateToStr(raw.devisDateDemarrageGo),
      devisDuree:           raw.devisDuree != null ? +raw.devisDuree : null,
      devisProvisionFrais:  raw.devisProvisionFrais != null ? +raw.devisProvisionFrais : null,
      devisPrixUoPropose:   raw.devisPrixUoPropose != null
                              ? Math.round(+raw.devisPrixUoPropose * 100) / 100
                              : null,
      devisCommentaire:     raw.devisCommentaire ?? null,
      devisNumeroContrat:   raw.devisNumeroContrat ?? null,
      devisNomContrat:      raw.devisNomContrat ?? null,
      devisBeneficiaire:    raw.devisBeneficiaire ?? null,
      dateEntretien:        this.dateToStr(raw.devisDateEntretien),

      devisDateFinTravaux:  this.data.devisDateFinTravaux ?? null,
      devisNumeroBonCommande: this.data.devisNumeroBonCommande ?? this.data.devisNumeroBdc ?? null,
      devisMotifRefus:      this.data.devisMotifRefus ?? null
    };

    this.devisService.update(this.data.devisId, request).subscribe({
      next: () => {
        this.snackbarService.openSnackBar('Devis enregistré avec succès');
        this.dialogRef.close(true);
      },
      error: () => {
        this.snackbarService.openSnackBar('Erreur lors de l\'enregistrement', 'error-snack-bar');
      }
    });
  }

  onEnvoyerAuSncf(): void {
    this.devisForm.markAllAsTouched();
    if (this.devisForm.get('devisDateEntretien')!.invalid) return;

    this.devisService.updateStatut(this.data.devisId, DevisStatut.EN_ATTENTE).subscribe({
      next: () => {
        this.devisService.updateFebStatut(this.data.febId, 'ATTENTEVALIDATIONSNCF').subscribe({
          next: () => {
            this.snackbarService.openSnackBar('Devis envoyé au SNCF avec succès');
            this.dialogRef.close(true);
          },
          error: () => {
            this.snackbarService.openSnackBar(
              'Devis envoyé, mais erreur lors de la mise à jour de la FEB',
              'error-snack-bar'
            );
            this.dialogRef.close(true);
          }
        });
      },
      error: () => {
        this.snackbarService.openSnackBar('Erreur lors de l\'envoi au SNCF', 'error-snack-bar');
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  get prixUoGrilleDisplay(): number | null | undefined {
    const majore = this.devisForm.get('devisMajore')?.value;
    return majore ? this.data.febPrixUoGrilleMajore : this.data.febPrixUoGrille;
  }

  get montantTotalCalcule(): number | null {
    const prix      = this.devisForm.get('devisPrixUoPropose')?.value;
    const duree     = this.devisForm.get('devisDuree')?.value;
    const provision = this.devisForm.get('devisProvisionFrais')?.value;
    if (prix == null || duree == null) return null;
    return Math.round((+prix * +duree + +(provision ?? 0)) * 100) / 100;
  }

  getStatutBadgeClass(): string {
    switch (this.data.devisStatut) {
      case DevisStatut.CREE:       return 'statut-cree';
      case DevisStatut.EN_ATTENTE: return 'statut-en-attente';
      case DevisStatut.ACCEPTE:    return 'statut-accepte';
      case DevisStatut.REFUSE:     return 'statut-refuse';
      case DevisStatut.ARCHIVE:    return 'statut-archive';
      default:                     return '';
    }
  }
}